import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Gift, Users, Copy, Check, Mail, Share2, TrendingUp,
  DollarSign, Star, Award
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useToast } from '../context/ToastContext';
import SEO from '../components/SEO';
import emailjs from '@emailjs/browser';

function ReferralProgram() {
  const { language } = useLanguage();
  const { showToast } = useToast();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [isGenerated, setIsGenerated] = useState(false);
  const [copied, setCopied] = useState(false);
  const [stats, setStats] = useState({
    clicks: 0,
    signups: 0,
    conversions: 0,
    earned: '€0'
  });

  const content = {
    sq: {
      title: 'Programi i Referimit',
      subtitle: 'Fitu duke i referuar shokët',
      hero: 'Fitu 10% për çdo klient të referuar',
      heroSub: 'Klienti juaj merr 5% zbritje, ju fitoni 10% nga projekti i tyre',
      howItWorks: 'Si funksionon?',
      step1: 'Gjenero kodin',
      step1Desc: 'Merr kodin personal të referimit',
      step2: 'Shpërndaj',
      step2Desc: 'Ndaje kodin me shokë dhe familje',
      step3: 'Fito',
      step3Desc: 'Merr 10% për çdo projekt të kompletuar',
      benefits: 'Përfitimet',
      benefit1: 'Zbritje 10% për referues',
      benefit2: 'Zbritje 5% për klientin e referuar',
      benefit3: 'Pa limit në numrin e referimeve',
      benefit4: 'Pagesa automatike çdo muaj',
      generateTitle: 'Gjenero Kodin Tënd',
      namePlaceholder: 'Emri juaj',
      emailPlaceholder: 'Email',
      generateButton: 'Gjenero Kodin',
      yourCode: 'Kodi juaj',
      copyCode: 'Kopjo',
      copied: 'Kopjuar!',
      shareTitle: 'Ndaje me:',
      stats: 'Statistikat',
      clicks: 'Klikime',
      signups: 'Regjistrimet',
      conversions: 'Konvertime',
      earned: 'Fituar',
      referralLink: 'Linku i Referimit',
      terms: 'Termat dhe Kushtet',
      term1: 'Referuesi merr 10% të vlerës së projektit kur klienti i referuar kompleton pagesën',
      term2: 'Klienti i referuar merr 5% zbritje në projektin e tij të parë',
      term3: 'Pagesa bëhen automatikisht çdo muaj për projektet e kompletuara',
      term4: 'Programi është i vlefshëm për të gjitha shërbimet e Contenta',
      term5: 'Contenta rezervon të drejtën për të ndryshuar termat pa njoftim paraprak'
    },
    en: {
      title: 'Referral Program',
      subtitle: 'Earn by referring friends',
      hero: 'Earn 10% for every referred client',
      heroSub: 'Your client gets 5% off, you earn 10% from their project',
      howItWorks: 'How It Works?',
      step1: 'Generate code',
      step1Desc: 'Get your personal referral code',
      step2: 'Share',
      step2Desc: 'Share the code with friends and family',
      step3: 'Earn',
      step3Desc: 'Get 10% for every completed project',
      benefits: 'Benefits',
      benefit1: '10% discount for referrer',
      benefit2: '5% discount for referred client',
      benefit3: 'No limit on referrals',
      benefit4: 'Automatic monthly payments',
      generateTitle: 'Generate Your Code',
      namePlaceholder: 'Your name',
      emailPlaceholder: 'Email',
      generateButton: 'Generate Code',
      yourCode: 'Your code',
      copyCode: 'Copy',
      copied: 'Copied!',
      shareTitle: 'Share with:',
      stats: 'Statistics',
      clicks: 'Clicks',
      signups: 'Signups',
      conversions: 'Conversions',
      earned: 'Earned',
      referralLink: 'Referral Link',
      terms: 'Terms and Conditions',
      term1: 'Referrer receives 10% of project value when referred client completes payment',
      term2: 'Referred client receives 5% discount on their first project',
      term3: 'Payments are made automatically monthly for completed projects',
      term4: 'Program is valid for all Contenta services',
      term5: 'Contenta reserves the right to change terms without prior notice'
    }
  };

  const t = content[language];

  const generateCode = async () => {
    if (!email || !name) {
      showToast(language === 'sq' ? 'Plotëso të gjitha fushat' : 'Fill all fields', 'error');
      return;
    }

    // Generate unique code
    const code = `${name.substring(0, 3).toUpperCase()}${Math.random().toString(36).substring(2, 8).toUpperCase()}`;
    setReferralCode(code);
    setIsGenerated(true);

    // Send email with code
    try {
      await emailjs.send(
        import.meta.env.VITE_EMAILJS_SERVICE_ID,
        import.meta.env.VITE_EMAILJS_REFERRAL_TEMPLATE_ID || 'template_referral',
        {
          to_email: email,
          to_name: name,
          referral_code: code,
          referral_link: `https://contenta.marketing?ref=${code}`
        },
        import.meta.env.VITE_EMAILJS_PUBLIC_KEY
      );

      showToast(language === 'sq' ? 'Kodi u gjenërua me sukses!' : 'Code generated successfully!', 'success');
    } catch (error) {
      console.error('EmailJS error:', error);
    }

    // Simulate stats (Replace with Firebase in production)
    setTimeout(() => {
      setStats({
        clicks: Math.floor(Math.random() * 50),
        signups: Math.floor(Math.random() * 10),
        conversions: Math.floor(Math.random() * 3),
        earned: `€${Math.floor(Math.random() * 500)}`
      });
    }, 1000);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(referralCode);
    setCopied(true);
    showToast(language === 'sq' ? 'Kodi u kopjua!' : 'Code copied!', 'success');
    setTimeout(() => setCopied(false), 2000);
  };

  const shareViaEmail = () => {
    const subject = encodeURIComponent('Check out Contenta - Get 5% OFF!');
    const body = encodeURIComponent(`Hi! I'm using Contenta for my business and thought you might be interested. Use my referral code "${referralCode}" to get 5% off your first project!\n\nVisit: https://contenta.marketing?ref=${referralCode}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  return (
    <>
      <SEO title={t.title} description={t.subtitle} />
      
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-20">
        <div className="container mx-auto px-4">
          {/* Hero */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <Gift className="w-16 h-16 mx-auto mb-6 text-yellow-600" />
            <h1 className="text-4xl sm:text-5xl font-bold mb-4 bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">
              {t.hero}
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-300">
              {t.heroSub}
            </p>
          </motion.div>

          {/* How It Works */}
          <div className="max-w-5xl mx-auto mb-16">
            <h2 className="text-3xl font-bold text-center text-gray-900 dark:text-white mb-12">
              {t.howItWorks}
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                { icon: Users, title: t.step1, desc: t.step1Desc },
                { icon: Share2, title: t.step2, desc: t.step2Desc },
                { icon: DollarSign, title: t.step3, desc: t.step3Desc }
              ].map((step, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700 text-center"
                >
                  <div className="w-16 h-16 bg-gradient-to-r from-yellow-500 to-yellow-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <step.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                    {step.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">{step.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Generate Code */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 border border-gray-200 dark:border-gray-700">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                {t.generateTitle}
              </h2>
              
              {!isGenerated ? (
                <div className="space-y-4">
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t.namePlaceholder}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-yellow-500 dark:bg-gray-700 dark:text-white"
                  />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t.emailPlaceholder}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-yellow-500 dark:bg-gray-700 dark:text-white"
                  />
                  <button
                    onClick={generateCode}
                    className="w-full bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-white px-8 py-4 rounded-lg font-semibold shadow-lg transform hover:-translate-y-1 transition-all flex items-center justify-center gap-2"
                  >
                    <Gift className="w-5 h-5" />
                    {t.generateButton}
                  </button>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 border-2 border-yellow-500">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{t.yourCode}</p>
                    <div className="flex items-center gap-3">
                      <code className="flex-1 text-3xl font-bold text-yellow-600 font-mono">
                        {referralCode}
                      </code>
                      <button
                        onClick={copyToClipboard}
                        className="bg-yellow-500 hover:bg-yellow-600 text-white p-3 rounded-lg transition-all"
                      >
                        {copied ? <Check className="w-6 h-6" /> : <Copy className="w-6 h-6" />}
                      </button>
                    </div>
                  </div>

                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{t.referralLink}</p>
                    <code className="text-sm text-gray-700 dark:text-gray-300 break-all">
                      https://contenta.marketing?ref={referralCode}
                    </code>
                  </div>

                  <div>
                    <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                      {t.shareTitle}
                    </p>
                    <div className="flex gap-3">
                      <button
                        onClick={shareViaEmail}
                        className="flex-1 bg-blue-500 hover:bg-blue-600 text-white px-4 py-3 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all"
                      >
                        <Mail className="w-5 h-5" />
                        Email
                      </button>
                      <button
                        onClick={() => window.open(`https://wa.me/?text=Use my code ${referralCode} for 5% OFF at Contenta!`, '_blank')}
                        className="flex-1 bg-green-500 hover:bg-green-600 text-white px-4 py-3 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all"
                      >
                        <Share2 className="w-5 h-5" />
                        WhatsApp
                      </button>
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 rounded-lg p-6 text-white">
                    <h3 className="text-lg font-bold mb-4">{t.stats}</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-3xl font-bold">{stats.clicks}</div>
                        <div className="text-sm opacity-90">{t.clicks}</div>
                      </div>
                      <div>
                        <div className="text-3xl font-bold">{stats.signups}</div>
                        <div className="text-sm opacity-90">{t.signups}</div>
                      </div>
                      <div>
                        <div className="text-3xl font-bold">{stats.conversions}</div>
                        <div className="text-sm opacity-90">{t.conversions}</div>
                      </div>
                      <div>
                        <div className="text-3xl font-bold">{stats.earned}</div>
                        <div className="text-sm opacity-90">{t.earned}</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Benefits & Terms */}
            <div className="space-y-6">
              {/* Benefits */}
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8 border border-gray-200 dark:border-gray-700">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
                  <Star className="w-6 h-6 text-yellow-600" />
                  {t.benefits}
                </h2>
                <ul className="space-y-4">
                  {[t.benefit1, t.benefit2, t.benefit3, t.benefit4].map((benefit, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <Check className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                      <span className="text-gray-700 dark:text-gray-300">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Terms */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-2xl p-6 border border-gray-200 dark:border-gray-600">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
                  {t.terms}
                </h3>
                <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                  {[t.term1, t.term2, t.term3, t.term4, t.term5].map((term, idx) => (
                    <li key={idx}>• {term}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default ReferralProgram;